import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { AuthShell, Field } from "./AuthShell";
import { TwoFactorStep } from "./TwoFactorStep";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const { login } = useAuth();
  const nav = useNavigate();

  const finish = (u) => {
    if (u.role !== "admin" && u.role !== "super_admin") {
      toast.error("This account does not have admin access.");
      setLoading(false);
      return;
    }
    toast.success("Admin authenticated");
    nav("/admin", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await login(email, password);
      if (r.requires_2fa) { setChallenge(r); setLoading(false); return; }
      finish(r);
    } catch (err) { toast.error(formatApiError(err)); setLoading(false); }
  };

  if (challenge) return <TwoFactorStep challenge={challenge} onSuccess={finish} onCancel={() => setChallenge(null)} />;

  return (
    <AuthShell title="Admin Console" subtitle="Restricted access — authorized personnel only">
      <div className="glass rounded-xl p-3 mb-5 flex items-center gap-3 border-cyan-500/20">
        <ShieldCheck className="w-5 h-5 text-cyan-400" />
        <p className="text-xs text-slate-400">Secure server-side role verification is enforced.</p>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Admin Email" testId="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@aitechnology.com" />
        <Field label="Password" testId="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        <button type="submit" disabled={loading} data-testid="admin-login-submit"
          className="btn-glow w-full py-3 rounded-lg font-semibold text-slate-950 inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Access Console
        </button>
      </form>
    </AuthShell>
  );
}
